import { WithChildren } from '@/types/common'
import { createContext } from '@/ultils'
import { Breakpoint } from '@mui/material'
import { useCallback, useMemo, useReducer, useRef } from 'react'

export interface State {
  displayMenu: boolean
  displayModal: boolean
  displaySidebar: boolean
  allowCloseSidebar: boolean
  modalView: MODAL_VIEW
  sidebarView: SIDEBAR_VIEW
}

const initialState: State = {
  displayMenu: false,
  displayModal: false,
  displaySidebar: false,
  allowCloseSidebar: true,
  sidebarView: {
    type: '',
    id: '',
  },
  modalView: {
    type: '',
    id: '',
    interfaceId: '',
    onSuccess: undefined,
  },
}

export type SIDEBAR_VIEW = {
  type: SIDEBAR_TYPES
  id?: string
  address?: string
  name?: string
  interfaceId?: number
  siteId?: string
  parentName?: string
  parentId?: string
  description?: string
  capacity?: number
  flowRuleId?: number
  pipeId?: number
  twiId?: number
  isParallelMode?: boolean
  data?: any
}
export type MODAL_VIEW = {
  type: MODAL_TYPES
  id?: string
  interfaceId?: string
  data?: any
  onSuccess?: () => void
  maxWidth?: Breakpoint
  stateStation?: string
  mode?: string
  pipeId?: string
  /** SCRUM-1032: the flow rule a pipe delete has to unwind first. */
  flowRuleId?: string
  /** SCRUM-1032: how many sub-pipes a pipe delete takes with it. */
  subPipeCount?: number
  fertigationMode?: string
  name?: string
}

type Action =
  | {
      type: 'OPEN_MENU'
    }
  | {
      type: 'CLOSE_MENU'
    }
  | {
      type: 'OPEN_SIDEBAR'
    }
  | {
      type: 'CLOSE_SIDEBAR'
      updater: (prevState: State) => Partial<State>
    }
  | {
      type: 'SET_SIDEBAR_VIEW'
      sidebarView: SIDEBAR_VIEW
    }
  | {
      type: 'OPEN_MODAL'
    }
  | {
      type: 'CLOSE_MODAL'
    }
  | {
      type: 'SET_MODAL_VIEW'
      modalView: MODAL_VIEW
    }
  | {
      type: 'UPDATE_STATE'
      updater: (prevState: State) => Partial<State>
    }

export type SIDEBAR_TYPES =
  | 'SITE_VIEW'
  | 'LOCATION_VIEW'
  | 'STATIONS_VIEW'
  | 'STATIONS_OVERVIEW'
  | 'ETZ_VIEW'
  | 'SENSOR_VIEW'
  | 'EMITTER_VIEW'
  | 'HISTORICAL_VIEW'
  | 'SOIL_VIEW'
  | 'CONTROLLERS_VIEW'
  | 'PLANT_VIEW'
  | 'PUMPS_VIEW'
  | 'PIPES_VIEW'
  | 'WEATHER_LINK_VIEW'
  | 'ADD_AREA_VIEW'
  | 'ADD_SUB_AREA_VIEW'
  | 'EDIT_AREA_VIEW'
  | 'EDIT_SUB_AREA_VIEW'
  | 'HYDRAULIC_VIEW'
  | 'FLOW_SENSOR_VIEW'
  | 'FLOW_VALVE_VIEW'
  | 'FERTIGATION_VIEW'
  | 'FERTIGATION_GLOBAL_EXCLUSION'
  | 'WATERSOURCE_VIEW'
  | 'CIS_FERTIGATION_VIEW'
  | 'TURFQUALITY_VIEW'
  | ''
export type MODAL_TYPES =
  | 'ADD_SITE_VIEW'
  | 'ADD_AREA_VIEW'
  | 'DELETE_PIPE_VIEW'
  | 'DELETE_PLANT_VIEW'
  | 'DELETE_AREA_VIEW'
  | 'DELETE_HYDRAULIC_VIEW'
  | 'DELETE_FLOW_SENSOR_VIEW'
  | 'DELETE_FLOW_VALVE_VIEW'
  | 'DELETE_FLOW_RULE_VIEW'
  | 'DELETE_SOIL_VIEW'
  | 'DELETE_EMITTER_VIEW'
  | 'DELETE_REFERENCE_VIEW'
  | 'DELETE_STATION_VIEW'
  | 'EDIT_SITE_VIEW'
  | 'EDIT_SENSOR_VIEW'
  | 'EDIT_VALVE_VIEW'
  | 'EDIT_WEATHER_VIEW'
  | 'CHANGE_PASSWORD_VIEW'
  | 'CONTROLLER_MODAL'
  | 'NOTIFICATION_MODAL'
  | 'DELETE_FERTIGATION'
  | 'DELETE_CIS_FERTIGATION'
  | 'UPDATE_MODE_CIS_FERTIGATION'
  | 'STATION_MODAL'
  | 'DELETE_WATER_SOURCE_VIEW'
  | 'DELETE_ETZ_VIEW'
  | 'DELETE_TURF_QUALITY_VIEW'
  | ''

interface ConfigContextValues extends State {
  // Menu
  openMenu: () => void
  closeMenu: () => void
  toggleMenu: () => void

  // Sidebar
  openSidebar: () => void
  closeSidebar: () => void
  setSidebarView: (sidebarView: SIDEBAR_VIEW) => void
  openModal: () => void
  closeModal: () => void
  setModalView: (modalView: MODAL_VIEW) => void
  setAllowSidebar: (value: boolean) => void

  /**
   * SCRUM-1032 QC item 6 — let a sidebar view refuse an accidental dismissal.
   *
   * The drawer is shared by every config view, and closing it on a backdrop
   * click throws away whatever was typed. A view that holds a form takes the
   * lock while it is open; the drawer then ignores backdrop and Escape, and the
   * view's own Cancel button becomes the only way out. Kept in a ref rather
   * than state: this is read at the moment of a dismissal, and re-rendering
   * every config screen whenever a drawer opens would be a poor trade.
   */
  lockSidebarDismiss: (locked: boolean) => void
  isSidebarDismissLocked: () => boolean
}

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    // Menu
    case 'OPEN_MENU': {
      return {
        ...state,
        displayMenu: true,
      }
    }
    case 'CLOSE_MENU': {
      return {
        ...state,
        displayMenu: false,
      }
    }

    // Sidebar
    case 'OPEN_SIDEBAR': {
      return {
        ...state,
        displaySidebar: true,
        allowCloseSidebar: true,
      }
    }
    case 'CLOSE_SIDEBAR': {
      const updates = action.updater(state)
      return {
        ...state,
        ...updates,
      }
    }
    case 'SET_SIDEBAR_VIEW': {
      return {
        ...state,
        sidebarView: {
          ...action.sidebarView,
        },
      }
    }
    case 'OPEN_MODAL': {
      return {
        ...state,
        displayModal: true,
      }
    }
    case 'CLOSE_MODAL': {
      return {
        ...state,
        displayModal: false,
      }
    }
    case 'SET_MODAL_VIEW': {
      return {
        ...state,
        modalView: action.modalView,
      }
    }
    case 'UPDATE_STATE': {
      const updates = action.updater(state)
      return {
        ...state,
        ...updates,
      }
    }
  }
}

const [Provider, useConfigUI] = createContext<ConfigContextValues>({
  name: 'ConfigUI',
})

const ConfigUIProvider = ({ children }: WithChildren) => {
  const [state, dispatch] = useReducer(uiReducer, initialState)

  // Whether the open sidebar view refuses backdrop / Escape dismissal.
  const sidebarDismissLockedRef = useRef(false)
  const lockSidebarDismiss = useCallback((locked: boolean) => {
    sidebarDismissLockedRef.current = locked
  }, [])
  const isSidebarDismissLocked = useCallback(
    () => sidebarDismissLockedRef.current,
    [],
  )

  // Menu

  const openMenu = useCallback(
    () => dispatch({ type: 'OPEN_MENU' }),
    [dispatch],
  )

  const closeMenu = useCallback(
    () => dispatch({ type: 'CLOSE_MENU' }),
    [dispatch],
  )

  const toggleMenu = useCallback(
    () =>
      state.displayMenu
        ? dispatch({ type: 'CLOSE_MENU' })
        : dispatch({ type: 'OPEN_MENU' }),
    [dispatch, state.displayMenu],
  )

  // Sidebar

  const openSidebar = useCallback(
    () => dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch],
  )

  const closeSidebar = useCallback(() => {
    dispatch({
      type: 'CLOSE_SIDEBAR',
      updater: (prevState: State) => {
        if (!prevState.allowCloseSidebar) return {}
        return {
          displaySidebar: false,
          allowCloseSidebar: true,
        }
      },
    })
  }, [dispatch])

  const setSidebarView = useCallback(
    (sidebarView: SIDEBAR_VIEW) =>
      dispatch({ type: 'SET_SIDEBAR_VIEW', sidebarView }),
    [dispatch],
  )

  const openModal = useCallback(
    () => dispatch({ type: 'OPEN_MODAL' }),
    [dispatch],
  )

  const closeModal = useCallback(
    () => dispatch({ type: 'CLOSE_MODAL' }),
    [dispatch],
  )

  const setModalView = useCallback(
    (modalView: MODAL_VIEW) => dispatch({ type: 'SET_MODAL_VIEW', modalView }),
    [dispatch],
  )

  // function update state

  const setAllowSidebar = useCallback(
    (value: boolean) => {
      dispatch({
        type: 'UPDATE_STATE',
        updater: () => ({
          allowCloseSidebar: value,
        }),
      })
    },
    [dispatch],
  )

  const value = useMemo(
    () => ({
      ...state,
      openMenu,
      closeMenu,
      toggleMenu,
      openSidebar,
      closeSidebar,
      setSidebarView,
      openModal,
      closeModal,
      setModalView,
      setAllowSidebar,
      lockSidebarDismiss,
      isSidebarDismissLocked,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  )

  return <Provider value={value}>{children}</Provider>
}

export { ConfigUIProvider, useConfigUI }
